/**
 * Russian labels and severity tones for every enum in the API contract.
 *
 * Two rules encoded here:
 *  1. no identifier from the wire is ever shown to an operator — this module
 *     is the single place that turns `PARTIALLY_FILLED` into
 *     «Частично исполнен»;
 *  2. severity is never carried by colour alone. Each `tone` is paired with a
 *     `glyph`, and the label text itself says what the state is, so the panel
 *     stays readable in greyscale and to a colour-blind operator.
 */

import type {
  BacktestStatus,
  CommandStatus,
  ConnectionStatus,
  DecisionOutcome,
  Mode,
  OrderKind,
  OrderStatus,
  Role,
  Side,
  StrategyState,
  TradeState,
  WarningLevel,
} from '../api/schemas';

export type Tone = 'neutral' | 'info' | 'ok' | 'warn' | 'danger';

export const TONE_GLYPH: Readonly<Record<Tone, string>> = Object.freeze({
  neutral: '•',
  info: 'i',
  ok: '✓',
  warn: '!',
  danger: '✕',
});

// --- Mode ------------------------------------------------------------------

export const MODE_LABEL: Readonly<Record<Mode, string>> = Object.freeze({
  PAPER: 'PAPER',
  VST: 'VST',
  LIVE: 'LIVE',
});

/** The long form used in the header badge and in confirmation dialogs. */
export const MODE_DESCRIPTION: Readonly<Record<Mode, string>> = Object.freeze({
  PAPER: 'бумажный режим, сделки не отправляются на площадку',
  VST: 'демо-счёт BingX, деньги не настоящие',
  LIVE: 'РЕАЛЬНЫЕ ДЕНЬГИ',
});

export const MODE_TONE: Readonly<Record<Mode, Tone>> = Object.freeze({
  PAPER: 'neutral',
  VST: 'warn',
  LIVE: 'danger',
});

// --- Strategy state --------------------------------------------------------

export const STRATEGY_STATE_LABEL: Readonly<Record<StrategyState, string>> = Object.freeze({
  RUNNING: 'Работает',
  ENTRIES_PAUSED: 'Входы на паузе',
  STOPPED: 'Остановлена',
  ERROR: 'Ошибка',
});

export const STRATEGY_STATE_TONE: Readonly<Record<StrategyState, Tone>> = Object.freeze({
  RUNNING: 'ok',
  ENTRIES_PAUSED: 'warn',
  STOPPED: 'neutral',
  ERROR: 'danger',
});

export const STRATEGY_STATE_HINT: Readonly<Record<StrategyState, string>> = Object.freeze({
  RUNNING: 'Плановые решения выполняются, новые входы разрешены.',
  ENTRIES_PAUSED: 'Новые входы не выполняются. Мониторинг и защита позиции продолжаются.',
  STOPPED: 'Плановые решения не выполняются.',
  ERROR: 'Движок сообщил об ошибке. Требуется вмешательство оператора.',
});

// --- Connections -----------------------------------------------------------

export const CONNECTION_STATUS_LABEL: Readonly<Record<ConnectionStatus, string>> = Object.freeze({
  CONNECTED: 'Подключено',
  DEGRADED: 'С ограничениями',
  DISCONNECTED: 'Нет связи',
});

export const CONNECTION_STATUS_TONE: Readonly<Record<ConnectionStatus, Tone>> = Object.freeze({
  CONNECTED: 'ok',
  DEGRADED: 'warn',
  DISCONNECTED: 'danger',
});

export const CONNECTION_KIND_LABEL = Object.freeze({
  MARKET_DATA: 'Рыночные данные',
  EXECUTION_VENUE: 'Торговая площадка',
  TELEGRAM: 'Telegram',
} as const);

// --- Side / decision -------------------------------------------------------

export const SIDE_LABEL: Readonly<Record<Side, string>> = Object.freeze({
  LONG: 'Лонг',
  SHORT: 'Шорт',
});

export const DECISION_LABEL: Readonly<Record<DecisionOutcome, string>> = Object.freeze({
  LONG: 'Вход в лонг',
  SHORT: 'Вход в шорт',
  SKIP: 'Без сделки',
});

export const DECISION_TONE: Readonly<Record<DecisionOutcome, Tone>> = Object.freeze({
  LONG: 'ok',
  SHORT: 'info',
  SKIP: 'neutral',
});

// --- Orders ----------------------------------------------------------------

export const ORDER_STATUS_LABEL: Readonly<Record<OrderStatus, string>> = Object.freeze({
  PENDING: 'Ожидает отправки',
  OPEN: 'В работе',
  PARTIALLY_FILLED: 'Частично исполнен',
  FILLED: 'Исполнен',
  CANCELLED: 'Отменён',
  EXPIRED: 'Истёк',
  REJECTED: 'Отклонён',
  // NOT «неизвестно»: the operator has to DO something about this one.
  UNKNOWN: 'Требуется сверка',
});

/**
 * Why `UNKNOWN` is `danger` and not `neutral`.
 *
 * It does not mean "we have not looked". It means the venue's answer was lost,
 * so the order may be resting on the exchange right now, unhedged and
 * unaccounted for. That needs the operator's attention as urgently as a
 * rejection does, and the label says so in words as well as in colour.
 */
export const ORDER_STATUS_TONE: Readonly<Record<OrderStatus, Tone>> = Object.freeze({
  PENDING: 'neutral',
  OPEN: 'info',
  PARTIALLY_FILLED: 'warn',
  FILLED: 'ok',
  CANCELLED: 'neutral',
  EXPIRED: 'neutral',
  REJECTED: 'danger',
  UNKNOWN: 'danger',
});

/** The explanation shown next to an `UNKNOWN` order. */
export const ORDER_STATUS_UNKNOWN_HINT =
  'Площадка не подтвердила состояние этого ордера. Он может быть активен. ' +
  'Сверьте его в терминале площадки перед любыми действиями с позицией.';

export const ORDER_KIND_LABEL: Readonly<Record<OrderKind, string>> = Object.freeze({
  ENTRY: 'Вход',
  STOP_LOSS: 'Stop Loss',
  TAKE_PROFIT: 'Take Profit',
  EMERGENCY_CLOSE: 'Аварийное закрытие',
  DUST_CLEANUP: 'Очистка остатка',
  UNKNOWN: 'Назначение не распознано',
});

/**
 * An order that can still be cancelled — drives the «отменить ордера» copy.
 *
 * `UNKNOWN` is in this set deliberately. An order whose state the venue never
 * confirmed may well be working, and excluding it would grey out the cancel
 * button on exactly the order most likely to need cancelling.
 */
export const CANCELLABLE_ORDER_STATUSES: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
  'PENDING',
  'OPEN',
  'PARTIALLY_FILLED',
  'UNKNOWN',
]);

// --- Warnings / incidents --------------------------------------------------

export const WARNING_LEVEL_LABEL: Readonly<Record<WarningLevel, string>> = Object.freeze({
  INFO: 'Информация',
  WARNING: 'Предупреждение',
  CRITICAL: 'Критично',
});

export const WARNING_LEVEL_TONE: Readonly<Record<WarningLevel, Tone>> = Object.freeze({
  INFO: 'info',
  WARNING: 'warn',
  CRITICAL: 'danger',
});

// --- Backtests -------------------------------------------------------------

export const BACKTEST_STATUS_LABEL: Readonly<Record<BacktestStatus, string>> = Object.freeze({
  QUEUED: 'В очереди',
  RUNNING: 'Выполняется',
  COMPLETED: 'Завершён',
  FAILED: 'Ошибка',
});

export const BACKTEST_STATUS_TONE: Readonly<Record<BacktestStatus, Tone>> = Object.freeze({
  QUEUED: 'neutral',
  RUNNING: 'info',
  COMPLETED: 'ok',
  FAILED: 'danger',
});

// --- Indicators ------------------------------------------------------------

export const INDICATOR_MODE_LABEL = Object.freeze({
  NORMAL: 'Обычный режим',
  POST_IMPULSE: 'Режим после импульса',
} as const);

// --- Audit -----------------------------------------------------------------

export const AUDIT_SOURCE_LABEL = Object.freeze({
  WEB: 'Веб-панель',
  TELEGRAM: 'Telegram',
  SYSTEM: 'Система',
} as const);

/**
 * Audit actions stay an OPEN vocabulary — the audit trail is append-only and
 * replays rows written by every build that ever ran, so a closed enum would
 * blank the whole page on one unfamiliar token. Known ones are translated; an
 * unknown one keeps its raw token *after* a Russian prefix, because silently
 * dropping an action from an audit trail is worse than showing an identifier.
 *
 * The keys below are the DOMAIN's `AuditEventType`, mirrored in the contract as
 * `KnownAuditActionSchema`. They previously spelled an invented vocabulary
 * (`LOGIN_SUCCESS`, `START_STRATEGY`, `CONNECTION_CHECK`…) that the server has
 * never emitted, so in practice every single audit row rendered as
 * «Другое действие (COMMAND_EXECUTED)» — the fallback working perfectly on a
 * table that matched nothing.
 */
const AUDIT_ACTION_RU: Readonly<Record<string, string>> = Object.freeze({
  DECISION_EVALUATED: 'Решение вычислено',
  ENTRY_SUBMITTED: 'Ордер на вход отправлен',
  ENTRY_FILLED: 'Вход исполнен',
  PROTECTION_CONFIRMED: 'Защитные ордера подтверждены',
  PROTECTION_FAILED: 'Не удалось выставить защитные ордера',
  POSITION_CLOSED: 'Позиция закрыта',
  ORDER_CANCELED: 'Ордер отменён',
  STATE_TRANSITION: 'Смена состояния сделки',
  RECONCILIATION_RUN: 'Сверка с площадкой выполнена',
  RECONCILIATION_MISMATCH: 'Расхождение при сверке с площадкой',
  IMPULSE_REGISTERED: 'Зафиксирован импульс',
  COMMAND_RECEIVED: 'Команда получена',
  COMMAND_EXECUTED: 'Команда выполнена',
  COMMAND_REJECTED: 'Команда отклонена',
  CONFIG_CHANGED: 'Изменение параметров стратегии',
  AUTH_LOGIN_SUCCESS: 'Успешный вход',
  AUTH_LOGIN_FAILURE: 'Неуспешный вход',
  LIVE_GATE_EVALUATED: 'Проверка допуска к реальной торговле',
  INCIDENT_OPENED: 'Инцидент открыт',
  INCIDENT_RESOLVED: 'Инцидент закрыт',
  NOTIFICATION_SENT: 'Уведомление отправлено',
  NOTIFICATION_FAILED: 'Уведомление не доставлено',
});

export function auditActionRu(action: string): string {
  return AUDIT_ACTION_RU[action] ?? `Другое действие (${action})`;
}

// --- Roles -----------------------------------------------------------------

export const ROLE_LABEL: Readonly<Record<Role, string>> = Object.freeze({
  VIEWER: 'Наблюдатель',
  OPERATOR: 'Оператор',
  ADMIN: 'Администратор',
});

// --- Trade lifecycle state (CLOSED) ----------------------------------------
//
// `positionState` is the domain `TradeState`, so the contract closes it to that
// enum and this table is exhaustive — `Record<TradeState, string>` means adding
// a state to the machine without a Russian label is a compile error.

export const TRADE_STATE_LABEL: Readonly<Record<TradeState, string>> = Object.freeze({
  IDLE: 'Позиции нет',
  DECISION_PENDING: 'Решение вычисляется',
  SKIPPED: 'Вход пропущен',
  ENTRY_SUBMITTING: 'Вход отправлен, исполнение не подтверждено',
  ENTRY_PARTIALLY_FILLED: 'Вход исполнен частично',
  PROTECTION_SUBMITTING: 'Защитные ордера выставляются',
  POSITION_PROTECTED: 'Позиция открыта и защищена',
  EXIT_PARTIALLY_FILLED: 'Выход исполнен частично',
  CLOSED_TP: 'Закрыта по Take Profit',
  CLOSED_SL: 'Закрыта по Stop Loss',
  CLOSED_MANUAL: 'Закрыта вручную',
  CLOSED_LIQUIDATION: 'Ликвидирована',
  EMERGENCY_CLOSING: 'Аварийное закрытие в процессе',
  ERROR_REQUIRES_ATTENTION: 'Ошибка: требуется вмешательство',
});

/**
 * `null` arrives when the server read a stored value that is not a
 * `TradeState`. It says so, rather than picking the nearest plausible state.
 */
export function positionStateRu(value: TradeState | null): string {
  if (value === null) return 'Состояние не распознано';
  return TRADE_STATE_LABEL[value];
}

// --- Open vocabulary --------------------------------------------------------
//
// `cooldownState` is a free-form string owned by the strategy engine, with no
// enum behind it, so it stays open and keeps its defensive fallback. Known
// values are translated; an unrecognised one is reported as unrecognised rather
// than leaked to the operator as an English identifier or dropped.

const COOLDOWN_STATE_RU: Readonly<Record<string, string>> = Object.freeze({
  NONE: 'Паузы нет',
  INACTIVE: 'Паузы нет',
  IDLE: 'Паузы нет',
  ACTIVE: 'Пауза активна',
  EXPIRED: 'Пауза завершена',
  POST_IMPULSE: 'Первый вход после паузы',
});

export function cooldownStateRu(value: string | null): string {
  if (value === null) return 'Паузы нет';
  return COOLDOWN_STATE_RU[value] ?? `Состояние не распознано (${value})`;
}

// --- Commands ---------------------------------------------------------------

export const COMMAND_STATUS_LABEL: Readonly<Record<CommandStatus, string>> = Object.freeze({
  PENDING: 'Выполняется',
  EXECUTED: 'Выполнена',
  REJECTED: 'Отклонена',
  FAILED: 'Ошибка выполнения',
});

/**
 * Russian for the tri-state `CommandResult.venueConfirmed`.
 *
 * `null` is not «нет»: the command never touched the venue, so there is nothing
 * to confirm. Rendering that as "not confirmed" would put a standing warning on
 * every pause and resume — and a warning that is always on is a warning nobody
 * reads, including on the one command where it matters.
 */
export function venueConfirmationRu(confirmed: boolean | null): {
  readonly tone: Tone;
  readonly titleRu: string;
  readonly detailRu: string;
} {
  if (confirmed === true) {
    return {
      tone: 'ok',
      titleRu: 'Команда подтверждена площадкой.',
      detailRu: '',
    };
  }
  if (confirmed === false) {
    return {
      tone: 'warn',
      titleRu: 'Команда принята, подтверждение площадки не получено.',
      detailRu:
        'Результат на площадке неизвестен. Сверьте позицию и ордера в терминале площадки, ' +
        'прежде чем повторять команду.',
    };
  }
  return {
    tone: 'ok',
    titleRu: 'Команда выполнена.',
    detailRu: 'Команда изменяет только состояние планировщика и не затрагивает площадку.',
  };
}

// --- Live-stream health ----------------------------------------------------

export type LiveStreamState = 'connecting' | 'live' | 'polling';

export const LIVE_STREAM_LABEL: Readonly<Record<LiveStreamState, string>> = Object.freeze({
  connecting: 'Поток событий: подключение…',
  live: 'Поток событий: активен',
  polling: 'Поток событий недоступен — обновление опросом',
});

export const LIVE_STREAM_TONE: Readonly<Record<LiveStreamState, Tone>> = Object.freeze({
  connecting: 'neutral',
  live: 'ok',
  polling: 'warn',
});
